import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { organizationService } from '../../services/organizationService';
import { userService } from '../../services/userService';

interface CreateOrganizationProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateOrganization = ({ open, onClose, onSuccess }: CreateOrganizationProps) => {
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      setError('You must be logged in to create an organization');
      return;
    }

    if (!name.trim()) {
      setError('Organization name is required');
      return;
    }

    try {
      console.log('Starting organization creation...', { name, userId: user.id });
      setError('');
      setLoading(true);

      // Check if user already has an organization
      if (user.organizationId) {
        throw new Error('You already belong to an organization');
      }
      
      // Create the organization
      const organizationId = await organizationService.createOrganization(name.trim(), user.id);
      console.log('Organization created successfully', { organizationId });

      // Refresh the user context to get the updated organization ID
      const updatedUser = await userService.getUserById(user.id);
      if (updatedUser) {
        // Force a reload of the auth context
        window.location.reload();
      }
      
      setName('');
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error in CreateOrganization component:', err);
      if (err instanceof Error) {
        setError(`Failed to create organization: ${err.message}`);
      } else {
        setError('Failed to create organization. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName('');
    setError('');
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          position: 'relative',
        },
      }}
    >
      <DialogTitle>Create New Organization</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Organization Name"
            type="text"
            fullWidth
            required
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError(''); // Clear error when user types
            }}
            disabled={loading}
            error={!!error}
            helperText={error ? ' ' : 'Enter your organization name'}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={handleClose} 
            disabled={loading}
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Box sx={{ position: 'relative' }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !name.trim()}
            >
              Create
            </Button>
            {loading && (
              <CircularProgress
                size={24}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
            )}
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateOrganization;
